interface HintTimingSeries {
  data: (number | null)[];
  label?: string;
  connectNulls?: boolean;
}

export function generateHintTimingSeriesData(
  numberOfGames: number = 100,
  maxHintsPerGame: number = 4,
  gameMaxDuration: number = 600 // 10 minuti in secondi
): HintTimingSeries[] {
  const series: HintTimingSeries[] = [];

  // Genera una serie per ogni singola partita
  for (let game = 0; game < numberOfGames; game++) {
    // Numero casuale di aiuti per questa partita (1-4)
    const hintsCount = Math.floor(Math.random() * maxHintsPerGame) + 1;
    const dataArray = new Array(21).fill(null); // 21 intervalli da 30s = 630s

    let previousTime = 0;
    
    // Genera i timing per ogni aiuto in questa partita
    for (let hintIndex = 0; hintIndex < hintsCount; hintIndex++) {
      // Il primo aiuto può essere chiesto dopo almeno 30 secondi
      const minTime = hintIndex === 0 ? 30 : previousTime + 20;
      const maxTime = Math.min(
        gameMaxDuration - (hintsCount - hintIndex - 1) * 30, // Lascia spazio per gli aiuti successivi
        gameMaxDuration * 0.8 // Non oltre l'80% della durata massima
      );

      // Timing casuale con distribuzione realistica
      const timing = Math.floor(
        minTime + Math.random() * (maxTime - minTime)
      );

      // Metti il numero dell'aiuto nel punto temporale corrispondente
      const timeIndex = Math.floor(timing / 30);
      if (timeIndex < 21) {
        dataArray[timeIndex] = hintIndex + 1; // Numero dell'aiuto (1, 2, 3, 4)
      }
      
      previousTime = timing;
    }

    // Ogni partita è una serie separata
    series.push({
      data: dataArray,
      connectNulls: true // Connette i punti saltando i null
    });
  }

  return series;
}

// Funzione helper per generare dati più specifici per il grafico degli aiuti
export function generateHintsDistributionData(): HintTimingSeries[] {
  const games: HintTimingSeries[] = [];

  // Genera 30 partite con pattern diversi e tempi variabili
  for (let i = 0; i < 30; i++) {
    const gameType = Math.random();
    
    if (gameType < 0.4) {
      // 40% - Giocatori che chiedono 1 solo aiuto
      const firstHintTime = 60 + Math.random() * 180; // Tra 1 e 4 minuti, variabile!
      const dataArray = new Array(21).fill(null);
      const timeIndex = Math.floor(firstHintTime / 30);
      if (timeIndex < 21) dataArray[timeIndex] = 1;
      games.push({
        data: dataArray,
        connectNulls: true
      });
    } else if (gameType < 0.7) {
      // 30% - Giocatori che chiedono 2 aiuti
      const firstHintTime = 45 + Math.random() * 120;
      const secondHintTime = firstHintTime + 60 + Math.random() * 120;
      const dataArray = new Array(21).fill(null);
      const firstIndex = Math.floor(firstHintTime / 30);
      const secondIndex = Math.floor(secondHintTime / 30);
      if (firstIndex < 21) dataArray[firstIndex] = 1;
      if (secondIndex < 21) dataArray[secondIndex] = 2;
      games.push({
        data: dataArray,
        connectNulls: true
      });
    } else if (gameType < 0.9) {
      // 20% - Giocatori che chiedono 3 aiuti
      const firstHintTime = 90 + Math.random() * 60;
      const secondHintTime = firstHintTime + 90 + Math.random() * 60;
      const thirdHintTime = secondHintTime + 120 + Math.random() * 60;
      const dataArray = new Array(21).fill(null);
      const firstIndex = Math.floor(firstHintTime / 30);
      const secondIndex = Math.floor(secondHintTime / 30);
      const thirdIndex = Math.floor(thirdHintTime / 30);
      if (firstIndex < 21) dataArray[firstIndex] = 1;
      if (secondIndex < 21) dataArray[secondIndex] = 2;
      if (thirdIndex < 21) dataArray[thirdIndex] = 3;
      games.push({
        data: dataArray,
        connectNulls: true
      });
    } else {
      // 10% - Giocatori che chiedono 4 aiuti
      const firstHintTime = 60 + Math.random() * 30;
      const secondHintTime = firstHintTime + 60 + Math.random() * 30;
      const thirdHintTime = secondHintTime + 120 + Math.random() * 60;
      const fourthHintTime = thirdHintTime + 120 + Math.random() * 120;
      const dataArray = new Array(21).fill(null);
      const firstIndex = Math.floor(firstHintTime / 30);
      const secondIndex = Math.floor(secondHintTime / 30);
      const thirdIndex = Math.floor(thirdHintTime / 30);
      const fourthIndex = Math.floor(fourthHintTime / 30);
      if (firstIndex < 21) dataArray[firstIndex] = 1;
      if (secondIndex < 21) dataArray[secondIndex] = 2;
      if (thirdIndex < 21) dataArray[thirdIndex] = 3;
      if (fourthIndex < 21) dataArray[fourthIndex] = 4;
      games.push({
        data: dataArray,
        connectNulls: true
      });
    }
  }

  return games;
}

// Nuova funzione per Timeline Chart - NON TOCCA NULLA DEL RESTO!
export function generateTimelineData() {
  const timelineData: any[] = [];

  // Genera 20 partite per il timeline
  for (let i = 0; i < 20; i++) {
    const gameType = Math.random();
    const gameId = `Partita ${i + 1}`;
    
    if (gameType < 0.4) {
      // 1 solo aiuto
      const hintTime = 60 + Math.random() * 180;
      timelineData.push({
        id: gameId,
        data: [
          {
            x: hintTime,
            y: gameId,
            hint: 1
          }
        ]
      });
    } else if (gameType < 0.7) {
      // 2 aiuti
      const firstHint = 45 + Math.random() * 120;
      const secondHint = firstHint + 60 + Math.random() * 120;
      timelineData.push({
        id: gameId,
        data: [
          { x: firstHint, y: gameId, hint: 1 },
          { x: secondHint, y: gameId, hint: 2 }
        ]
      });
    } else if (gameType < 0.9) {
      // 3 aiuti
      const firstHint = 90 + Math.random() * 60;
      const secondHint = firstHint + 90 + Math.random() * 60;
      const thirdHint = secondHint + 120 + Math.random() * 60;
      timelineData.push({
        id: gameId,
        data: [
          { x: firstHint, y: gameId, hint: 1 },
          { x: secondHint, y: gameId, hint: 2 },
          { x: thirdHint, y: gameId, hint: 3 }
        ]
      });
    } else {
      // 4 aiuti
      const firstHint = 60 + Math.random() * 30;
      const secondHint = firstHint + 60 + Math.random() * 30;
      const thirdHint = secondHint + 120 + Math.random() * 60;
      const fourthHint = thirdHint + 120 + Math.random() * 120;
      timelineData.push({
        id: gameId,
        data: [
          { x: firstHint, y: gameId, hint: 1 },
          { x: secondHint, y: gameId, hint: 2 },
          { x: thirdHint, y: gameId, hint: 3 },
          { x: fourthHint, y: gameId, hint: 4 }
        ]
      });
    }
  }

  return timelineData;
}
